import React, { FC } from 'react';

import { List, ListProps, ListItemProps } from '@lglp/components';
import { LinkType } from '@lglp/prismic-client';
import { values } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import { HEADER } from 'translations';

import { EN, FR, getLangTranslation } from '../../../helpers/i18n';
import { getDomain } from '../../../helpers/root';
import { getAlternateLanguages } from '../../../helpers/seo';
import { LinkedComponent } from '../../LinkedComponent';
import styles from './MenuLocales.module.scss';

const TRANSLATIONS: Record<string, string> = {
  [FR]: HEADER.WORDING_LANGFR,
  [EN]: HEADER.WORDING_LANGEN,
};

type Alternate = {
  locale?: string;
  disabled?: boolean;
  to?: string;
};

type MenuLocalesItemProps = ListItemProps & {
  alternate?: Alternate;
  currentLocale: string;
};

const MenuLocalesItem: FC<MenuLocalesItemProps> = ({
  alternate,
  currentLocale,
  ...rest
}) => {
  const { t } = useTranslation();
  const { locale, disabled, to } = alternate;
  return (
    <List.Item
      className={[styles.locale, currentLocale === locale && styles.active]}
      {...rest}
    >
      <LinkedComponent
        className={styles.link}
        link={{
          linkType: LinkType.Web,
          url: disabled
            ? [getDomain(), locale].join('/')
            : [getDomain(), to].join(''),
        }}
        disabled={currentLocale === locale}
        {...(currentLocale !== locale ? { hrefLang: locale } : undefined)}
        title={t(
          disabled ? HEADER.MISSING_TRANSLATION : HEADER.ALTERNATE_TRANSLATION,
          { lang: t(getLangTranslation(locale)) }
        )}
      >
        {t(TRANSLATIONS[locale])}
      </LinkedComponent>
    </List.Item>
  );
};

export const MenuLocales: FC<ListProps> = ({ className, ...rest }) => {
  const { i18n } = useTranslation();
  const match = useRouteMatch();
  const currentLocale = i18n.language;

  const alternates = useSelector<{
    main?: { document: any };
  }>((state) => getAlternateLanguages(state.main?.document, match)) as Record<
    string,
    Alternate
  >;

  if (!alternates) {
    return null;
  }

  return (
    <List className={[styles.localsMenu, className]} {...rest}>
      {values(alternates).map((alternate, index) => (
        <MenuLocalesItem
          key={index}
          currentLocale={currentLocale}
          alternate={alternate}
        />
      ))}
    </List>
  );
};
